import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Switch } from '../ui/switch';
import {
  Zap,
  Lock,
  Unlock,
  TrendingUp,
  Gift,
  Users,
  RefreshCw,
  Crown,
  Diamond,
  Clock,
  Layers,
  CheckCircle,
  Info,
} from 'lucide-react';
import {
  YieldBoost,
  UserBoostStatus,
  YIELD_BOOSTS,
  BOOST_TYPE_COLORS,
} from '../../types/staking-boost';

interface YieldBoostPanelProps {
  currentApy: number;
  stakedAmount: string;
  userBoosts?: UserBoostStatus[];
  onActivateBoost?: (boostId: string, params?: any) => void;
}

export function YieldBoostPanel({
  currentApy,
  stakedAmount,
  userBoosts = [],
  onActivateBoost,
}: YieldBoostPanelProps) {
  const [selectedLockDuration, setSelectedLockDuration] = useState<number | null>(null);
  const [autoCompound, setAutoCompound] = useState(false);

  // Calculate boosted APY based on active boosts
  const calculateBoostedApy = () => {
    let totalBonus = 0;
    const activeBoostIds = userBoosts
      .filter((ub) => ub.isActive)
      .map((ub) => ub.boostId);

    YIELD_BOOSTS.forEach((boost) => {
      if (activeBoostIds.includes(boost.id)) {
        totalBonus += boost.bonusApy;
      }
    });

    // Add selected lock duration if any
    if (selectedLockDuration) {
      const lockBoost = YIELD_BOOSTS.find(
        (b) => b.type === 'lock_duration' && b.requirement.value === selectedLockDuration
      );
      if (lockBoost && !activeBoostIds.includes(lockBoost.id)) {
        totalBonus += lockBoost.bonusApy;
      }
    }

    // Add compound boost if enabled
    if (autoCompound) {
      const compoundBoost = YIELD_BOOSTS.find((b) => b.type === 'compound');
      if (compoundBoost && !activeBoostIds.includes(compoundBoost.id)) {
        totalBonus += compoundBoost.bonusApy;
      }
    }

    return currentApy + totalBonus;
  };

  const boostedApy = calculateBoostedApy();
  const apyIncrease = boostedApy - currentApy;

  const getBoostIcon = (boost: YieldBoost) => {
    switch (boost.type) {
      case 'lock_duration':
        return <Lock className="h-4 w-4" />;
      case 'token_amount':
        return <TrendingUp className="h-4 w-4" />;
      case 'loyalty':
        return <Diamond className="h-4 w-4" />;
      case 'compound':
        return <RefreshCw className="h-4 w-4" />;
      case 'referral':
        return <Users className="h-4 w-4" />;
      case 'governance':
        return <Crown className="h-4 w-4" />;
      case 'early_adopter':
        return <Gift className="h-4 w-4" />;
      case 'multi_chain':
        return <Layers className="h-4 w-4" />;
      default:
        return <Zap className="h-4 w-4" />;
    }
  };

  const isBoostActive = (boostId: string) => {
    return userBoosts.some((ub) => ub.boostId === boostId && ub.isActive);
  };

  const lockDurationBoosts = YIELD_BOOSTS.filter((b) => b.type === 'lock_duration');
  const otherBoosts = YIELD_BOOSTS.filter((b) => b.type !== 'lock_duration');

  return (
    <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-purple-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          Yield Boost
          <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 ml-2">
            +{apyIncrease.toFixed(1)}% Extra
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* APY Display */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400">APY Base</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {currentApy}%
            </p>
          </div>
          <div className="p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg text-center border border-amber-500/30">
            <p className="text-sm text-amber-600 dark:text-amber-400">APY con Boost</p>
            <p className="text-2xl font-bold text-amber-500">
              {boostedApy.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Lock Duration Selection */}
        <div className="space-y-3">
          <h4 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Lock className="h-4 w-4 text-blue-500" />
            Periodo de Bloqueo
          </h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Bloquea tus tokens para obtener mayor APY. Puedes retirar en cualquier momento sin bloqueo.
          </p>

          <div className="grid grid-cols-4 gap-2">
            <Button
              variant={selectedLockDuration === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedLockDuration(null)}
              className="flex flex-col h-auto py-2"
            >
              <Unlock className="h-4 w-4 mb-1" />
              <span className="text-xs">Flexible</span>
              <span className="text-xs text-slate-500">+0%</span>
            </Button>
            {lockDurationBoosts.map((boost) => (
              <Button
                key={boost.id}
                variant={selectedLockDuration === boost.requirement.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLockDuration(boost.requirement.value as number)}
                className="flex flex-col h-auto py-2"
              >
                <Lock className="h-4 w-4 mb-1" />
                <span className="text-xs">{boost.requirement.value}d</span>
                <span className="text-xs text-green-500">+{boost.bonusApy}%</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Auto-Compound Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <RefreshCw className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">Auto-Compound</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Reinvertir recompensas automaticamente (+1.5% APY)
              </p>
            </div>
          </div>
          <Switch
            checked={autoCompound}
            onCheckedChange={setAutoCompound}
          />
        </div>

        {/* Other Available Boosts */}
        <div className="space-y-3">
          <h4 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Gift className="h-4 w-4 text-pink-500" />
            Boosts Adicionales
          </h4>

          <div className="space-y-2">
            {otherBoosts.filter(b => b.type !== 'compound').map((boost) => {
              const isActive = isBoostActive(boost.id);
              const userBoost = userBoosts.find((ub) => ub.boostId === boost.id);

              return (
                <div
                  key={boost.id}
                  className={`p-3 rounded-lg border transition-all ${
                    isActive
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-slate-50 dark:bg-slate-800 border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${BOOST_TYPE_COLORS[boost.type]}`}>
                        {getBoostIcon(boost)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {boost.name}
                          </p>
                          {isActive && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          {boost.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                        +{boost.bonusApy}%
                      </Badge>
                    </div>
                  </div>

                  {/* Progress for progressive boosts */}
                  {userBoost?.progress !== undefined && userBoost.progress < 100 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Progreso</span>
                        <span>{userBoost.progress}%</span>
                      </div>
                      <Progress value={userBoost.progress} className="h-1" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Estimated Rewards */}
        <div className="p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-lg border border-violet-500/20">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-violet-500" />
            <span className="font-medium text-slate-900 dark:text-slate-100">
              Recompensas Estimadas
            </span>
          </div>
          {parseFloat(stakedAmount) > 0 ? (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400">Diario</p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">
                  {((parseFloat(stakedAmount) * (boostedApy / 100)) / 365).toFixed(2)} NDH
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400">Mensual</p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">
                  {((parseFloat(stakedAmount) * (boostedApy / 100)) / 12).toFixed(2)} NDH
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400">Anual</p>
                <p className="font-semibold text-green-500">
                  {(parseFloat(stakedAmount) * (boostedApy / 100)).toFixed(2)} NDH
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
              Stakea tokens para ver recompensas estimadas
            </p>
          )}
        </div>

        {/* Info Note */}
        <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <Info className="h-4 w-4 text-blue-500 mt-0.5" />
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Los boosts se activan automaticamente cuando cumples los requisitos.
            Algunos boosts son acumulables para mayor rendimiento.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
